import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { FullDateDto } from "@/types/fullDate";

/**
 * The dates the admin has marked as fully booked, for the notice on the public
 * bid request form.
 *
 * Everything here is built so that this call can never cost us a lead:
 *
 * - it uses a bare axios rather than the shared axiosInstance, because that
 *   instance redirects the browser to /signin on any 401. A prospective
 *   customer being thrown off the form by a background fetch would be far worse
 *   than not showing the notice.
 * - a failure resolves to an empty list instead of throwing, so the form renders
 *   exactly as it did before this feature existed.
 * - nothing on the form waits on it. It is advisory only.
 */
export function useFullDates() {
  const { data } = useQuery({
    queryKey: ["full-dates"],
    queryFn: async (): Promise<FullDateDto[]> => {
      try {
        const response = await axios.get<FullDateDto[]>(
          `${process.env.REACT_APP_BASE_API_URL}full-dates`,
        );
        return Array.isArray(response.data) ? response.data : [];
      } catch {
        // Deliberately swallowed. See the note above.
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return data ?? [];
}
